import { useMemo } from 'react';
import { VStack } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';
import { StudentEnrolmentHealth, StudentRecentPaceStatus } from '@duke-hq/svc-api-db';

import buildLinkToEnrolments from '../../utils/buildLinkToEnrolments';
import ContentContainer from './shared/ContentContainer';
import SidePanelContentHeader from './shared/ContentHeader';
import HealthStatCard from './shared/HealthStatCard';
import NoDataMessage from './shared/NoDataMessage';

type Props = {
  health?: StudentEnrolmentHealth;
};

function RecentPaceStatusData({ health }: Props) {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getRecentPaceCounts.useQuery(
    { filters, health },
    { suspense: true },
  );

  if (!data || Object.keys(data).length === 0) {
    return <NoDataMessage />;
  }

  return (
    <>
      <SidePanelContentHeader
        title={t`14-day engagement`}
        tooltipText={t`Participant progress in the last 14 days`}
        to={buildLinkToEnrolments({
          dueDateStart: djs.utc(),
          dueDateEnd: djs.utc().add(28, 'days'),
          health,
        })}
      />

      <VStack>
        <HealthStatCard
          title={t`Great`}
          description={t`More than 1 assessment complete`}
          descriptionColour="blue.500"
          backgroundColour="blue.50"
          value={data.great}
          to={buildLinkToEnrolments({
            dueDateStart: djs.utc(),
            dueDateEnd: djs.utc().add(28, 'days'),
            health,
            recentPaceStatus: StudentRecentPaceStatus.GREAT,
          })}
        />
        <HealthStatCard
          title={t`Good`}
          description={t`1 assessment complete`}
          descriptionColour="green.500"
          backgroundColour="green.50"
          value={data.good}
          to={buildLinkToEnrolments({
            dueDateStart: djs.utc(),
            dueDateEnd: djs.utc().add(28, 'days'),
            health,
            recentPaceStatus: StudentRecentPaceStatus.GOOD,
          })}
        />
        <HealthStatCard
          title={t`Slow`}
          description={t`Unit content progressed`}
          descriptionColour="orange.500"
          backgroundColour="orange.50"
          value={data.slow}
          to={buildLinkToEnrolments({
            dueDateStart: djs.utc(),
            dueDateEnd: djs.utc().add(28, 'days'),
            health,
            recentPaceStatus: StudentRecentPaceStatus.SLOW,
          })}
        />
        <HealthStatCard
          title={t`None`}
          description={t`No course activity`}
          descriptionColour="red.500"
          backgroundColour="red.50"
          value={data.none}
          to={buildLinkToEnrolments({
            dueDateStart: djs.utc(),
            dueDateEnd: djs.utc().add(28, 'days'),
            health,
            recentPaceStatus: StudentRecentPaceStatus.NONE,
          })}
        />
      </VStack>
    </>
  );
}

export default function TwoWeekEngagement(props: Props) {
  const title = useMemo(() => {
    switch (props.health) {
      case StudentEnrolmentHealth.BEHIND:
        return t`Next 4 weeks - Behind`;
      case StudentEnrolmentHealth.SLIPPING:
        return t`Next 4 weeks - Slipping`;
      case StudentEnrolmentHealth.ON_TRACK:
        return t`Next 4 weeks - On track`;
      default:
        return '';
    }
  }, [props.health]);

  return (
    <ContentContainer title={title}>
      <RecentPaceStatusData {...props} />
    </ContentContainer>
  );
}
